import React, { useMemo, useCallback } from 'react'
import I18n from 'i18n'
import moment from 'moment/moment'
import Slider from 'components/common/Slider'

// Cache the pivot day outside component to avoid recreating it
const SLIDER_PIVOT = moment('2021-09-20').startOf('day')
const SLIDER_ID = 'date-filter-slider'

const CoachLoadDaySlider = React.memo((props) => {
  const { minDate, maxDate, sliderStartDate, sliderEndDate, smartSetDateRange } = props

  // Memoize slider range calculation to avoid recalculating on every render
  const sliderRange = useMemo(() => {
    if (!minDate || !maxDate) return undefined

    // The slider track represents the full 28-day range available for selection
    const rangeStartDate = minDate.clone().startOf('day')
    const rangeEndDate = maxDate.clone().endOf('day')

    // Avoids selection of the same day for min/max, which causes an error with the slider
    const oneDayBeforeEndDate = rangeEndDate.clone().subtract(1, 'day')
    const isDateValid = rangeStartDate.isSameOrBefore(oneDayBeforeEndDate)

    if (isDateValid) {
      // Return the full slider track range (what the user can select from)
      return [
        rangeStartDate.diff(SLIDER_PIVOT, 'days'),
        rangeEndDate.diff(SLIDER_PIVOT, 'days')
      ]
    }
    return undefined
  }, [minDate, maxDate])

  // Memoize slider handle positions
  const sliderHandlePositions = useMemo(() => {
    if (!sliderStartDate || !sliderEndDate) return [0, 1]

    const currentStartDay = sliderStartDate.clone().startOf('day')
    const currentEndDay = sliderEndDate.clone().endOf('day')
    return [
      currentStartDay.diff(SLIDER_PIVOT, 'days'),
      currentEndDay.diff(SLIDER_PIVOT, 'days')
    ]
  }, [sliderStartDate, sliderEndDate])

  // Memoize day range notation to avoid recalculating date formatting
  const dayRangeNotation = useMemo(() => {
    if (!sliderStartDate || !sliderEndDate) return ''

    const startDate = sliderStartDate.format('MMM D')
    const endDate = sliderEndDate.format('MMM D')
    const startYear = sliderStartDate.year()
    const endYear = sliderEndDate.year()
    const daysDiff = sliderEndDate.diff(sliderStartDate, 'days') + 1

    if (startYear === endYear) {
      return I18n.t('components.dashboards.coach_individual.load.slider.day_range_same_year', {
        dayCount: daysDiff,
        startDate,
        endDate,
        year: startYear
      })
    }
    return I18n.t('components.dashboards.coach_individual.load.slider.day_range_different_years', {
      dayCount: daysDiff,
      startDate,
      startYear,
      endDate,
      endYear
    })
  }, [sliderStartDate, sliderEndDate])

  // Memoize slider formatter to avoid recreating function on every render
  const sliderFormatter = useCallback((dayDifference) => {
    const selectedMoment = SLIDER_PIVOT.clone().add(dayDifference, 'days')
    return selectedMoment.format('MMM D')
  }, [])

  // Memoize date range change handler
  const handleDateRangeChange = useCallback((_range, _handle, unencodedRange) => {
    const newStart = SLIDER_PIVOT.clone().add(unencodedRange[0], 'days')
    const newEnd = SLIDER_PIVOT.clone().add(unencodedRange[1], 'days')
    smartSetDateRange(newStart, newEnd)
  }, [smartSetDateRange])

  return (
    <div className='col s12 no-noui-tooltips'>
      <Slider
        id={SLIDER_ID}
        range={sliderRange || [0, 1]}
        start={sliderHandlePositions}
        onChange={handleDateRangeChange}
        title={I18n.t('components.dashboards.coach_individual.load.slider.days', { value: dayRangeNotation })}
        customFormatter={sliderFormatter}
        disabled={!sliderRange}
        largerLabel
      />
    </div>
  )
})

export default CoachLoadDaySlider
